import { Box, Button, Divider } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import type { FilterColorScheme, FilterOption } from "./quote-filters.const";
import {
  STATUS_FILTERS,
  STATUS_SCHEME,
  DEPOSIT_FILTERS,
  DEPOSIT_SCHEME,
} from "./quote-filters.const";
import type { QuoteStatusFilter, DepositFilter } from "./quote-filters.types";

/* ═══════════════════════════════════════════════════════════
   FilterChip – single reusable chip for any filter category
   ═══════════════════════════════════════════════════════════ */

function FilterChip({
  label,
  icon: Icon,
  active,
  onClick,
  scheme,
}: {
  label: string;
  icon: SvgIconComponent;
  active: boolean;
  onClick: () => void;
  scheme: FilterColorScheme;
}) {
  return (
    <Button
      onClick={onClick}
      disableRipple
      sx={{
        textTransform: "none",
        height: 30,
        px: 1.5,
        fontWeight: active ? 600 : 400,
        fontSize: 12,
        minWidth: "auto",
        borderRadius: "16px",
        backgroundColor: active ? scheme.activeBg : "transparent",
        border: `1px solid ${active ? scheme.activeBorder : "transparent"}`,
        color: active ? scheme.activeText : "text.secondary",
        transition: "all 120ms ease-in-out",
        "&:hover": {
          backgroundColor: active ? scheme.activeBg : scheme.hoverBg,
          border: `1px solid ${active ? scheme.activeBorder : scheme.hoverBorder}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Icon sx={{ fontSize: 16, color: active ? scheme.activeIcon : "text.disabled", transition: "color 120ms ease" }} />
        {label}
      </Box>
    </Button>
  );
}

/* ═══════════════════════════════════════════════════════════
   FilterGroup – renders a row of FilterChips (single-select)
   ═══════════════════════════════════════════════════════════ */

function FilterGroup<V extends string>({
  options,
  value,
  onChange,
  scheme,
}: {
  options: readonly FilterOption<V>[];
  value: V | undefined;
  onChange: (next: V | undefined) => void;
  scheme: FilterColorScheme;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {options.map((opt) => (
        <FilterChip
          key={opt.value}
          label={opt.label}
          icon={opt.icon}
          active={value === opt.value}
          onClick={() => onChange(value === opt.value ? undefined : opt.value)}
          scheme={scheme}
        />
      ))}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════
   QuoteFilters – main orchestrator
   ═══════════════════════════════════════════════════════════ */

interface Props {
  statusFilter: QuoteStatusFilter | undefined;
  onStatusChange: (val: QuoteStatusFilter | undefined) => void;
  depositFilter: DepositFilter | undefined;
  onDepositFilterChange: (val: DepositFilter | undefined) => void;
  hideFilters?: boolean;
}

export function QuoteFilters({
  statusFilter,
  onStatusChange,
  depositFilter,
  onDepositFilterChange,
  hideFilters = false,
}: Props) {
  const handleStatusChange = (val: QuoteStatusFilter | undefined) => {
    onStatusChange(!val || val === "ALL" ? undefined : val);
  };

  const handleDepositChange = (val: DepositFilter | undefined) => {
    onDepositFilterChange(val);
  };

  // Hide filters when in selection mode (creating jobs from quotes)
  if (hideFilters) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {/* Row: Status + Deposit */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <FilterGroup
          options={STATUS_FILTERS}
          value={statusFilter ?? "ALL"}
          onChange={handleStatusChange}
          scheme={STATUS_SCHEME}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        <FilterGroup
          options={DEPOSIT_FILTERS}
          value={depositFilter}
          onChange={handleDepositChange}
          scheme={DEPOSIT_SCHEME}
        />
      </Box>
    </Box>
  );
}
